import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import FormField, { fieldInputStyle } from "../components/ui/FormField";
import { listClientes } from "../services/clientesService";
import { listCategorias } from "../services/categoriasService";
import {
  fetchPorPeriodo,
  fetchPorCliente,
  fetchFluxoCaixa,
  openBlobInTab,
  downloadBlob,
  extractBlobErrorMessage,
} from "../services/relatoriosService";

// Tela de relatórios PDF do Financeiro — 3 abas: Por período · Por cliente ·
// Fluxo de caixa. Migrada do antigo RelatoriosModal (mesma lógica/handlers);
// só o invólucro mudou (modal → página com abas). Endpoints inalterados.
// A descrição de cada relatório vira o subtítulo embaixo da aba ativa.
const ABAS = [
  {
    id: "periodo",
    label: "Por período",
    descricao:
      "Lista lançamentos no período, agrupados por categoria, com subtotais e saldo final. Filtros opcionais de cliente e categoria.",
  },
  {
    id: "cliente",
    label: "Por cliente",
    descricao:
      "Receitas e despesas vinculadas a um cliente, com resumo de total quitado, em aberto e última movimentação.",
  },
  {
    id: "fluxo",
    label: "Fluxo de caixa",
    descricao:
      "Últimos 12 meses. Entradas, saídas e saldo por mês.",
  },
];

export default function RelatoriosPage() {
  const { C } = useTheme();
  const [abaAtiva, setAbaAtiva] = useState("periodo"); // default: Por período

  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);

  // Loading independente por botão — chave "{secao}-{preview|download}".
  // Permite paralelismo (visualizar um relatório enquanto baixa outro).
  const [busy, setBusy] = useState(new Set());

  // Por período
  const [periodoDe, setPeriodoDe] = useState("");
  const [periodoAte, setPeriodoAte] = useState("");
  const [periodoCliente, setPeriodoCliente] = useState("");
  const [periodoCategoriaId, setPeriodoCategoriaId] = useState("");

  // Por cliente
  const [clienteSel, setClienteSel] = useState("");

  // Erros de validação client-side, por seção.
  const [errors, setErrors] = useState({ periodo: null, cliente: null });

  useEffect(() => {
    listClientes().then(setClientes).catch(() => setClientes([]));
    listCategorias().then(setCategorias).catch(() => setCategorias([]));
  }, []);

  function isBusy(key) {
    return busy.has(key);
  }

  function setBusyFor(key, active) {
    setBusy((s) => {
      const n = new Set(s);
      if (active) n.add(key);
      else n.delete(key);
      return n;
    });
  }

  // Fluxo padrão dos 6 botões (3 seções × Visualizar/Baixar).
  //  - Preview: pre-abre tab ANTES do await (user gesture), seta location após
  //    fetch. Se bloqueado, alert + return.
  //  - Download: fetch + <a download>.
  async function runAction({ key, fetchFn, preview }) {
    let newTab = null;
    if (preview) {
      newTab = window.open("", "_blank");
      if (!newTab) {
        alert("Permita pop-ups pra visualizar o PDF.");
        return;
      }
    }
    setBusyFor(key, true);
    try {
      const { blob, filename } = await fetchFn();
      if (preview) openBlobInTab(newTab, blob);
      else downloadBlob(blob, filename);
    } catch (err) {
      if (newTab) newTab.close();
      const msg = (await extractBlobErrorMessage(err)) ?? "Erro ao gerar relatório.";
      alert(msg);
    } finally {
      setBusyFor(key, false);
    }
  }

  function validatePeriodo() {
    if (!periodoDe || !periodoAte) {
      setErrors((e) => ({ ...e, periodo: "Informe data início e fim." }));
      return false;
    }
    if (periodoAte < periodoDe) {
      setErrors((e) => ({ ...e, periodo: "Data final não pode ser anterior à inicial." }));
      return false;
    }
    setErrors((e) => ({ ...e, periodo: null }));
    return true;
  }

  function validateCliente() {
    if (!clienteSel) {
      setErrors((e) => ({ ...e, cliente: "Selecione um cliente." }));
      return false;
    }
    setErrors((e) => ({ ...e, cliente: null }));
    return true;
  }

  const periodoParams = () => ({
    de: periodoDe,
    ate: periodoAte,
    clienteId: periodoCliente || undefined,
    categoriaId: periodoCategoriaId || undefined,
  });

  const handlePeriodoPreview = () => {
    if (!validatePeriodo()) return;
    runAction({ key: "periodo-preview", preview: true, fetchFn: () => fetchPorPeriodo(periodoParams()) });
  };
  const handlePeriodoDownload = () => {
    if (!validatePeriodo()) return;
    runAction({ key: "periodo-download", preview: false, fetchFn: () => fetchPorPeriodo(periodoParams()) });
  };

  const handleClientePreview = () => {
    if (!validateCliente()) return;
    runAction({ key: "cliente-preview", preview: true, fetchFn: () => fetchPorCliente(clienteSel) });
  };
  const handleClienteDownload = () => {
    if (!validateCliente()) return;
    runAction({ key: "cliente-download", preview: false, fetchFn: () => fetchPorCliente(clienteSel) });
  };

  const handleFluxoPreview = () =>
    runAction({ key: "fluxo-preview", preview: true, fetchFn: fetchFluxoCaixa });
  const handleFluxoDownload = () =>
    runAction({ key: "fluxo-download", preview: false, fetchFn: fetchFluxoCaixa });

  const inputStyle = fieldInputStyle(C);
  const abaInfo = ABAS.find((a) => a.id === abaAtiva);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          Relatórios
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Gere PDFs financeiros pra análise, contador ou cliente
        </p>
      </div>

      {/* Tab bar — mesma pegada das abas do Financeiro */}
      <div
        style={{
          display: "flex",
          gap: 2,
          marginBottom: 16,
          background: C.bgCard,
          padding: 4,
          borderRadius: 10,
          border: `1px solid ${C.border}`,
          width: "fit-content",
          flexWrap: "wrap",
        }}
      >
        {ABAS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setAbaAtiva(tab.id)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: abaAtiva === tab.id ? C.accent : "transparent",
              color: abaAtiva === tab.id ? "#fff" : C.textMuted,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conteúdo da aba ativa — card com subtítulo descritivo + campos + ações */}
      <div
        style={{
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 20,
          maxWidth: 720,
        }}
      >
        <p style={{ fontSize: 12, color: C.textMuted, margin: "0 0 16px", lineHeight: 1.4 }}>
          {abaInfo?.descricao}
        </p>

        {abaAtiva === "periodo" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FormField label="De" required>
                <input
                  type="date"
                  value={periodoDe}
                  onChange={(e) => setPeriodoDe(e.target.value)}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Até" required>
                <input
                  type="date"
                  value={periodoAte}
                  onChange={(e) => setPeriodoAte(e.target.value)}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Cliente (opcional)">
                <select
                  value={periodoCliente}
                  onChange={(e) => setPeriodoCliente(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">(Todos)</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Categoria (opcional)">
                <select
                  value={periodoCategoriaId}
                  onChange={(e) => setPeriodoCategoriaId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">(Todas)</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </FormField>
            </div>
            {errors.periodo && <ErrorMsg C={C} msg={errors.periodo} />}
            <ActionButtons
              C={C}
              onPreview={handlePeriodoPreview}
              previewBusy={isBusy("periodo-preview")}
              onDownload={handlePeriodoDownload}
              downloadBusy={isBusy("periodo-download")}
            />
          </>
        )}

        {abaAtiva === "cliente" && (
          <>
            <FormField label="Cliente" required>
              <select
                value={clienteSel}
                onChange={(e) => setClienteSel(e.target.value)}
                style={inputStyle}
              >
                <option value="">Selecione...</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </FormField>
            {errors.cliente && <ErrorMsg C={C} msg={errors.cliente} />}
            <ActionButtons
              C={C}
              onPreview={handleClientePreview}
              previewBusy={isBusy("cliente-preview")}
              onDownload={handleClienteDownload}
              downloadBusy={isBusy("cliente-download")}
            />
          </>
        )}

        {abaAtiva === "fluxo" && (
          <ActionButtons
            C={C}
            onPreview={handleFluxoPreview}
            previewBusy={isBusy("fluxo-preview")}
            onDownload={handleFluxoDownload}
            downloadBusy={isBusy("fluxo-download")}
          />
        )}
      </div>
    </div>
  );
}

function ActionButtons({ C, onPreview, previewBusy, onDownload, downloadBusy }) {
  const base = {
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
  };
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
      <button
        type="button"
        onClick={onPreview}
        disabled={previewBusy}
        style={{
          ...base,
          border: "none",
          background: C.accent,
          color: "#fff",
          cursor: previewBusy ? "wait" : "pointer",
          opacity: previewBusy ? 0.6 : 1,
        }}
      >
        {previewBusy ? "Carregando..." : "👁️ Visualizar"}
      </button>
      <button
        type="button"
        onClick={onDownload}
        disabled={downloadBusy}
        style={{
          ...base,
          border: `1px solid ${C.accent}`,
          background: "transparent",
          color: C.accent,
          cursor: downloadBusy ? "wait" : "pointer",
          opacity: downloadBusy ? 0.6 : 1,
        }}
      >
        {downloadBusy ? "Baixando..." : "⬇ Baixar"}
      </button>
    </div>
  );
}

function ErrorMsg({ C, msg }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 12px",
        background: C.redDim,
        color: C.red,
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {msg}
    </div>
  );
}
